import * as React from "react";
import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Stack,
  Typography,
  Alert,
  ListItemIcon,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import WorkIcon from "@mui/icons-material/Work";
import SchoolIcon from "@mui/icons-material/School";
import FolderIcon from "@mui/icons-material/Folder";
import { useNavigate } from "react-router";
import PageContainer from "./PageContainer";
import { getAllResumes, deleteResume } from "../../services/resumeService";
import useNotifications from "../hooks/useNotifications/useNotifications";
import { useAuth } from "../../auth/AuthContext";

// Render arrays as boxes
const RenderBoxItems = ({ items }) => {
  if (!items || items.length === 0) return null;
  return (
    <Stack direction="row" flexWrap="wrap" sx={{ mt: 1 }}>
      {items.map((item, idx) => (
        <Box
          key={idx}
          sx={{
            px: 1.5,
            py: 0.5,
            borderRadius: 1,
            border: "1px solid",
            borderColor: "primary.main",
            color: "primary.main",
            fontSize: "0.875rem",
            fontWeight: 500,
            m: 0.5,
          }}
        >
          {item}
        </Box>
      ))}
    </Stack>
  );
};

// Timeline item
const TimelineItem = ({ title, subtitle, date, description, icon }) => (
  <Box sx={{ display: "flex", mb: 3 }}>
    <Box sx={{ mt: 0.5, mr: 2 }}>{icon}</Box>
    <Box>
      <Typography variant="subtitle1">{title}</Typography>
      {subtitle && <Typography variant="body2">{subtitle}</Typography>}
      {date && <Typography variant="caption">{date}</Typography>}
      {description && <Typography variant="body2">{description}</Typography>}
    </Box>
  </Box>
);

// Project or volunteer card
const CardItem = ({ item }) => {
  const hasContent =
    item.name || item.description || item.link || item.organization;
  if (!hasContent) return null;

  return (
    <Paper sx={{ p: 2 }} elevation={1}>
      {item.name && <Typography variant="subtitle1">{item.name}</Typography>}
      {item.organization && (
        <Typography variant="subtitle2">{item.organization}</Typography>
      )}
      {item.role && <Typography variant="body2">{item.role}</Typography>}
      {item.description && (
        <Typography variant="body2" sx={{ mt: 1 }}>
          {item.description}
        </Typography>
      )}
      {item.link && (
        <Typography variant="body2" sx={{ mt: 1 }}>
          <a href={item.link} target="_blank" rel="noopener noreferrer">
            {item.link}
          </a>
        </Typography>
      )}
      {item.techStack?.length > 0 && <RenderBoxItems items={item.techStack} />}
    </Paper>
  );
};

export default function ResumePage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const notifications = useNotifications();

  const [resume, setResume] = React.useState(null);
  const [isLoading, setIsLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);

  const loadResume = React.useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await getAllResumes();
      setResume(response?.message || null); // message contains parsedJson
    } catch (err) {
      setError(err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  React.useEffect(() => {
    loadResume();
  }, [loadResume]);

  const handleUpload = () => navigate("/app/user/resume/upload");

  const handleDeleteConfirm = async () => {
    if (!resume) return;
    try {
      await deleteResume();
      notifications.show("Resume deleted successfully", {
        severity: "success",
        autoHideDuration: 3000,
      });
      setDeleteDialogOpen(false);
      loadResume();
    } catch (err) {
      notifications.show(`Failed to delete resume: ${err.message}`, {
        severity: "error",
        autoHideDuration: 3000,
      });
    }
  };

  if (isLoading) {
    return (
      <PageContainer title="My Resume">
        <Box sx={{ display: "flex", justifyContent: "center", py: 5 }}>
          <CircularProgress />
        </Box>
      </PageContainer>
    );
  }

  if (error) {
    return (
      <PageContainer title="My Resume">
        <Alert severity="error">{error.message}</Alert>
      </PageContainer>
    );
  }

  if (!resume?.parsedJson) {
    return (
      <PageContainer title="My Resume">
        <Paper sx={{ p: 3, textAlign: "center" }} elevation={2}>
          <Typography variant="body1">
            You haven't uploaded a resume yet.
          </Typography>
          <Button
            variant="contained"
            startIcon={<UploadFileIcon />}
            sx={{ mt: 2 }}
            onClick={handleUpload}
          >
            Upload Resume
          </Button>
        </Paper>
      </PageContainer>
    );
  }

  const parsed = resume.parsedJson;

  const {
    name,
    email,
    phone,
    location,
    linkedin,
    github,
    portfolio,
    summary,
    objective,
    skills,
    certifications,
    languages,
    awards,
    interests,
    achievements,
    experience,
    projects,
    education,
    volunteer,
    publications,
  } = parsed;

  return (
    <PageContainer
      title={"My Resume"}
      breadcrumbs={[{ title: "My Resume", path: "/app/user/resume" }]}
      actions={[
        <Stack direction="row" alignItems="center" spacing={1}>
          {user?.role === "USER" && (
            <Button
              variant="contained"
              onClick={() => navigate("/app/user/resume/shortlist")}
              startIcon={<ListItemIcon />}
            >
              Shortlist Resume
            </Button>
          )}
        </Stack>,
      ]}
    >
      <Stack spacing={3} sx={{ width: "100%" }}>
        {/* Header */}
        {(name || email || phone || linkedin || github || portfolio || location) && (
          <Paper sx={{ p: 3 }} elevation={2}>
            {name && <Typography variant="h4">{name}</Typography>}
            <Stack spacing={0.5} sx={{ mt: 1 }}>
              {email && (
                <Typography>
                  Email:{" "}
                  <a
                    href={`mailto:${email}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {email}
                  </a>
                </Typography>
              )}
              {phone && <Typography>Phone: {phone}</Typography>}
              {linkedin && (
                <Typography>
                  LinkedIn:{" "}
                  <a
                    href={linkedin}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {linkedin}
                  </a>
                </Typography>
              )}
              {github && (
                <Typography>
                  GitHub:{" "}
                  <a
                    href={github}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {github}
                  </a>
                </Typography>
              )}
              {portfolio && (
                <Typography>
                  Portfolio:{" "}
                  <a
                    href={portfolio}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {portfolio}
                  </a>
                </Typography>
              )}
              {location && <Typography>Location: {location}</Typography>}
            </Stack>
          </Paper>
        )}

        {/* Summary & Objective */}
        {summary && (
          <Paper sx={{ p: 3 }} elevation={2}>
            <Typography variant="h6">Summary</Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              {summary}
            </Typography>
          </Paper>
        )}
        {objective && (
          <Paper sx={{ p: 3 }} elevation={2}>
            <Typography variant="h6">Objective</Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              {objective}
            </Typography>
          </Paper>
        )}

        {/* Boxes */}
        {skills?.length > 0 && (
          <Paper sx={{ p: 3 }} elevation={2}>
            <Typography variant="h6">Skills</Typography>
            <RenderBoxItems items={skills} />
          </Paper>
        )}
        {certifications?.length > 0 && (
          <Paper sx={{ p: 3 }} elevation={2}>
            <Typography variant="h6">Certifications</Typography>
            <RenderBoxItems items={certifications} />
          </Paper>
        )}
        {languages?.length > 0 && (
          <Paper sx={{ p: 3 }} elevation={2}>
            <Typography variant="h6">Languages</Typography>
            <RenderBoxItems items={languages} />
          </Paper>
        )}
        {awards?.length > 0 && (
          <Paper sx={{ p: 3 }} elevation={2}>
            <Typography variant="h6">Awards</Typography>
            <RenderBoxItems items={awards} />
          </Paper>
        )}
        {interests?.length > 0 && (
          <Paper sx={{ p: 3 }} elevation={2}>
            <Typography variant="h6">Interests</Typography>
            <RenderBoxItems items={interests} />
          </Paper>
        )}
        {achievements?.length > 0 && (
          <Paper sx={{ p: 3 }} elevation={2}>
            <Typography variant="h6">Achievements</Typography>
            <RenderBoxItems items={achievements} />
          </Paper>
        )}
        {publications?.length > 0 && (
          <Paper sx={{ p: 3 }} elevation={2}>
            <Typography variant="h6">Publications</Typography>
            <RenderBoxItems items={publications} />
          </Paper>
        )}

        {/* Experience */}
        {experience?.length > 0 && (
          <Paper sx={{ p: 3 }} elevation={2}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <WorkIcon color="primary" />
              <Typography variant="h6">Experience</Typography>
            </Stack>
            <Stack sx={{ mt: 2 }}>
              {experience.map((exp, idx) => (
                <TimelineItem
                  key={idx}
                  title={exp.role}
                  subtitle={exp.company}
                  date={`${exp.startDate || ""} - ${exp.endDate || "Present"}`}
                  description={exp.description}
                  icon={<WorkIcon />}
                />
              ))}
            </Stack>
          </Paper>
        )}

        {/* Education */}
        {education?.length > 0 && (
          <Paper sx={{ p: 3 }} elevation={2}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <SchoolIcon color="primary" />
              <Typography variant="h6">Education</Typography>
            </Stack>
            <Stack sx={{ mt: 2 }}>
              {education.map((edu, idx) => (
                <TimelineItem
                  key={idx}
                  title={edu.degree}
                  subtitle={edu.institution}
                  date={`${edu.startDate || ""} - ${edu.endDate || "Present"}`}
                  description={edu.description}
                  icon={<SchoolIcon />}
                />
              ))}
            </Stack>
          </Paper>
        )}

        {/* Projects */}
        {projects?.length > 0 && (
          <Paper sx={{ p: 3 }} elevation={2}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <FolderIcon color="primary" />
              <Typography variant="h6">Projects</Typography>
            </Stack>
            <Stack spacing={2} sx={{ mt: 1 }}>
              {projects.map((proj, idx) => (
                <CardItem key={idx} item={proj} />
              ))}
            </Stack>
          </Paper>
        )}

        {/* Volunteer */}
        {volunteer?.length > 0 && (
          <Paper sx={{ p: 3 }} elevation={2}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <FolderIcon color="primary" />
              <Typography variant="h6">Volunteer</Typography>
            </Stack>
            <Stack spacing={2} sx={{ mt: 1 }}>
              {volunteer.map((vol, idx) => (
                <CardItem key={idx} item={vol} />
              ))}
            </Stack>
          </Paper>
        )}

        {/* Actions */}
        <Stack direction="row" spacing={2}>
          <Button
            variant="contained"
            startIcon={<UploadFileIcon />}
            onClick={handleUpload}
          >
            Update Resume
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => setDeleteDialogOpen(true)}
          >
            Delete Resume
          </Button>
        </Stack>
      </Stack>

      {/* Delete Resume Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Resume</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete your resume? This action cannot be
            undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteConfirm}
          >
            Confirm Delete
          </Button>
        </DialogActions>
      </Dialog>
    </PageContainer>
  );
}
